import { SettingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Dropdown, Table } from "antd";
import type { ColumnsType, TablePaginationConfig, TableRowSelection } from "antd/es/table/interface";
import { useEffect, useMemo, useState, type Key } from "react";

type DataGridColumn<T> = ColumnsType<T>[number];
const pageSizeOptions = [100, 200, 300, 400, 500].map(String);

export function DataGrid<T extends { id?: string | number }>({
  rows,
  columns,
  loading,
  pagination,
  rowSelection,
  tableId,
  enableColumnSettings = true,
  onRowDoubleClick
}: {
  rows: T[];
  columns: ColumnsType<T>;
  loading?: boolean;
  pagination?: false | TablePaginationConfig;
  rowSelection?: TableRowSelection<T>;
  tableId?: string;
  enableColumnSettings?: boolean;
  onRowDoubleClick?: (row: T) => void;
}) {
  const pageSize = pagination && typeof pagination === "object" ? Number(pagination.pageSize ?? 100) : 100;
  const current = pagination && typeof pagination === "object" ? Number(pagination.current ?? 1) : 1;
  const rowNumberColumn = useMemo<DataGridColumn<T>>(() => ({
    key: "__rowNumber",
    title: "#",
    width: 56,
    fixed: "left" as const,
    align: "center" as const,
    render: (_: unknown, __: T, index: number) => ((current - 1) * pageSize) + index + 1
  }), [current, pageSize]);
  const allColumns = useMemo(() => [rowNumberColumn, ...columns], [columns, current, pageSize]);
  const derivedTableId = useMemo(() => tableId ?? buildTableId(allColumns), [allColumns, tableId]);
  const configurableColumns = useMemo(
    () => allColumns
      .map((column, index) => ({ column, key: getColumnKey(column, index), label: getColumnLabel(column, index) }))
      .filter((item) => item.key !== "__rowNumber" && !isMandatoryActionColumn(item.column) && item.label),
    [allColumns]
  );
  const [visibleKeys, setVisibleKeys] = usePersistedColumnKeys(derivedTableId, configurableColumns.map((item) => item.key));
  const visibleColumns = useMemo(
    () => allColumns.filter((column, index) => {
      const key = getColumnKey(column, index);
      return key === "__rowNumber" || isMandatoryActionColumn(column) || visibleKeys.includes(key);
    }),
    [allColumns, visibleKeys]
  );
  const scrollX = useMemo(() => Math.max(sumColumnWidths(visibleColumns), 920), [visibleColumns]);

  return (
    <div className="data-grid-shell">
      {enableColumnSettings && configurableColumns.length ? (
        <div className="data-grid-toolbar">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "columns",
                  label: (
                    <Checkbox.Group
                      className="column-chooser"
                      value={visibleKeys}
                      options={configurableColumns.map((item) => ({ label: item.label, value: item.key }))}
                      onChange={(keys) => setVisibleKeys(keys.map(String))}
                    />
                  )
                }
              ]
            }}
          >
            <Button size="small" icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        </div>
      ) : null}
      <Table<T>
        className={`data-grid ${onRowDoubleClick ? "clickable-rows" : ""}`}
        rowKey={(row) => String(row.id ?? JSON.stringify(row))}
        size="small"
        bordered
        sticky
        loading={loading}
        dataSource={rows}
        columns={visibleColumns}
        pagination={pagination === false ? false : { pageSize: 100, showSizeChanger: true, pageSizeOptions, ...(pagination ?? {}) }}
        rowSelection={rowSelection}
        scroll={{ x: scrollX }}
        onRow={(record) => ({
          onDoubleClick: () => onRowDoubleClick?.(record)
        })}
      />
    </div>
  );
}

function isMandatoryActionColumn<T>(column: DataGridColumn<T>) {
  return String((column as { key?: Key }).key ?? "").toLocaleLowerCase() === "actions";
}

function usePersistedColumnKeys(tableId: string, defaultKeys: string[]) {
  const storageKey = `accounting-spa:columns:${tableId}`;
  const [visibleKeys, setVisibleKeysState] = useState<string[]>(() => readStoredColumnKeys(storageKey, defaultKeys));

  useEffect(() => {
    setVisibleKeysState(readStoredColumnKeys(storageKey, defaultKeys));
  }, [storageKey, defaultKeys.join("|")]);

  const setVisibleKeys = (keys: string[]) => {
    const next = keys.filter((key) => defaultKeys.includes(key));
    const safeKeys = next.length ? next : defaultKeys;
    setVisibleKeysState(safeKeys);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(safeKeys));
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  };

  return [visibleKeys, setVisibleKeys] as const;
}

function readStoredColumnKeys(storageKey: string, defaultKeys: string[]) {
  if (typeof window === "undefined") return defaultKeys;
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : null;
    if (!Array.isArray(parsed)) return defaultKeys;
    const keys = parsed.map(String).filter((key) => defaultKeys.includes(key));
    return keys.length ? keys : defaultKeys;
  } catch {
    return defaultKeys;
  }
}

function getColumnKey<T>(column: DataGridColumn<T>, index: number) {
  if (index === 0) return "__rowNumber";
  const columnWithKey = column as { key?: Key; dataIndex?: Key | Key[] };
  if (columnWithKey.key !== undefined) return String(columnWithKey.key);
  if (Array.isArray(columnWithKey.dataIndex)) return columnWithKey.dataIndex.join(".");
  if (columnWithKey.dataIndex !== undefined) return String(columnWithKey.dataIndex);
  return `column-${index}`;
}

function getColumnLabel<T>(column: DataGridColumn<T>, index: number) {
  if (index === 0) return "#";
  const title = (column as { title?: unknown }).title;
  if (typeof title === "string") return title;
  if (typeof title === "number") return String(title);
  return index === 0 ? "#" : `Column ${index}`;
}

function buildTableId<T>(columns: DataGridColumn<T>[]) {
  return columns.map((column, index) => getColumnKey(column, index)).join(":");
}

function sumColumnWidths<T>(columns: DataGridColumn<T>[]) {
  return columns.reduce((total, column) => {
    const width = Number((column as { width?: number | string }).width);
    return total + (Number.isFinite(width) ? width : 180);
  }, 0);
}
